import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Mail, CheckCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@/lib/api';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await api.post('/auth/password/forgot', {
        email: email.trim(),
        app_origin: window.location.origin,
      });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'hsl(var(--bg-paper))' }}>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-sm px-4"
      >
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center gap-2 no-underline mb-6">
            <Shield className="h-6 w-6 text-ink" strokeWidth={1.5} />
            <span className="font-display text-lg font-medium text-ink">The Fashion Detective</span>
          </Link>
        </div>

        <Card className="bg-white border-stone/50 shadow-[var(--shadow-card)]">
          <CardContent className="p-6">
            {!sent ? (
              <>
                <h1 className="font-display text-xl font-medium text-ink mb-1">Reset Password</h1>
                <p className="text-xs text-muted-foreground mb-5">
                  Enter your email and we'll send you a password reset link.
                </p>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
                  <div>
                    <Label htmlFor="email" className="text-xs">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      className="mt-1"
                      data-testid="forgot-email-input"
                    />
                  </div>
                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription className="text-xs">{error}</AlertDescription>
                    </Alert>
                  )}
                  <Button
                    type="submit"
                    className="w-full gap-2 rounded-[12px] h-11 active:scale-[0.98]"
                    disabled={loading}
                    data-testid="forgot-submit-btn"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                </form>
              </>
            ) : (
              <div className="text-center py-4" data-testid="forgot-sent-state">
                <CheckCircle className="h-10 w-10 mx-auto mb-3" style={{ color: '#0E6B5D' }} />
                <h2 className="font-display text-lg font-medium text-ink mb-2">Check Your Email</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  If an account exists for <strong>{email}</strong>, we've sent a password reset link.
                </p>
              </div>
            )}
            <div className="text-center mt-3">
              <Link to="/account/sign-in" className="text-xs text-muted-foreground hover:text-ink no-underline">
                Back to Sign In
              </Link>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-4">
          <Link to="/" className="inline-flex items-center gap-1 text-xs text-muted-foreground no-underline hover:text-ink">
            <ArrowLeft className="h-3 w-3" /> Back to site
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
